import type { Page } from "./models/story";

export type StoryReaderLayout = "spread" | "single";

/** Pages currently shown (one on mobile, up to two on desktop). */
export function getVisiblePages(
  pages: Page[],
  cursorIndex: number,
  layout: StoryReaderLayout,
): Page[] {
  const n = pages.length;
  if (n === 0 || cursorIndex < 0 || cursorIndex >= n) return [];

  if (layout === "single") {
    return [pages[cursorIndex]];
  }

  const left = pages[cursorIndex];
  const right = pages[cursorIndex + 1];
  return right !== undefined ? [left, right] : [left];
}

export function canStepPrev(
  cursorIndex: number,
  layout: StoryReaderLayout,
): boolean {
  if (layout === "single") return cursorIndex > 0;
  return cursorIndex >= 2;
}

export function canStepNext(
  cursorIndex: number,
  layout: StoryReaderLayout,
  pageCount: number,
): boolean {
  if (pageCount === 0) return false;
  if (layout === "single") return cursorIndex < pageCount - 1;
  return cursorIndex + 2 < pageCount;
}

export function stepPrev(
  cursorIndex: number,
  layout: StoryReaderLayout,
): number | null {
  if (!canStepPrev(cursorIndex, layout)) return null;
  return layout === "single" ? cursorIndex - 1 : cursorIndex - 2;
}

export function stepNext(
  cursorIndex: number,
  layout: StoryReaderLayout,
  pageCount: number,
): number | null {
  if (!canStepNext(cursorIndex, layout, pageCount)) return null;
  return layout === "single" ? cursorIndex + 1 : cursorIndex + 2;
}

/** Align to an even index when switching to spread layout. */
export function snapCursorForSpread(cursorIndex: number): number {
  return Math.floor(cursorIndex / 2) * 2;
}
